<template>
	<v-navigation-drawer
		id="navigation-drawer"
		v-model="localeValue"
		app
		dark
		:mobile-breakpoint="$vuetify.breakpoint.thresholds.md"
		:right="$i18n.locale === 'ar' || $vuetify.rtl"
		color="primary"
		width="210"
		:permanent="$vuetify.breakpoint.mdAndUp"
		:mini-variant="$vuetify.breakpoint.mdAndUp && !localeValue"
		mini-variant-width="55"
	>
		<div class="d-flex flex-column justify-space-between" style="height: 100%">
			<div>
				<!-- brand image -->
				<v-list nav dense>
					<v-list-item
						class="justify-start"
						@click="$route.name !== 'home' && $router.push({ name: 'home' })"
					>
						<v-list-item-icon>
							<v-icon> mdi-home </v-icon>
						</v-list-item-icon>
						<div>
							<v-img :width="localeValue ? 60 : 40" cover src="@/assets/logo-white.png" />
						</div>
					</v-list-item>
					<v-divider class="mb-2" />
					<v-list-item
						v-for="(page, index) in pages.filter(page => page.show && !page.group)"
						:key="index"
						:to="typeof page.target === 'string' ? { name: page.target } : page.target"
						:disabled="page.disabled"
						exact-path
					>
						<v-list-item-icon class="me-0">
							<v-icon>{{page.icon}}</v-icon>
						</v-list-item-icon>
						<v-list-item-title class="ms-3 text-capitalize">
							{{page.title}}
						</v-list-item-title>
					</v-list-item>
				
					<v-list-group
						v-for="(group, index) in groupedPages"
						:key="group.title + index"
						active-class="white--text"
					>
						<template v-slot:activator>
							<v-list-item-icon class="me-0">
								<v-icon>{{group.icon}}</v-icon>
							</v-list-item-icon>
							<v-list-item-title class="ms-3 text-capitalize">
								{{group.title}}
							</v-list-item-title>
						</template>

						<v-list-item
							v-for="(page, index) in group.pages"
							:key="index"
							:to="typeof page.target === 'string' ? { name: page.target } : page.target"
							:disabled="page.disabled"
							active-class="white--text"
							exact-path
						>
							<v-list-item-icon class="me-0">
								<v-icon>{{page.icon}}</v-icon>
							</v-list-item-icon>
							<v-list-item-title class="ms-3 text-capitalize">
								{{page.title}}
							</v-list-item-title>
						</v-list-item>
					</v-list-group>
				</v-list>
			</div>
			<div v-if="localeValue" class="powered-by white--text px-2 pb-1">
				<span>Ugarit - v{{version}}</span>
				<p v-if="localeValue || $vuetify.breakpoint.smAndDown" class="mb-0">
					powered by
					<a class="white--text" target="_blank" href="https://www.beetronix.com">Beetronix</a>
				</p>
			</div>
		</div>
	</v-navigation-drawer>
</template>

<script>
import { VERSION } from '@/constants';
import { mapState } from 'vuex';
export default {
	name: 'NavigationDrawer',

	props: {
		value: { type: Boolean, default: false }
	},

	data: () => ({
		version: VERSION,
	}),

	computed: {
		...mapState({
			username: state => state.auth.self.username,
			isAdmin: state => state.auth.self.isAdmin,
			permissions: state => state.auth.self.permissions,
		}),
		
		groupedPages() {
			const groupsPages = [];

			const groups = [
				{
					name: 'productions',
					title: this.$t('components.productions'),
					icon: 'mdi-package-variant',
					show: !this.permissions.inventory.isNone
				},
				{
					name: 'warehouse',
					title: this.$t('components.warehouse'),
					icon: 'mdi-home-silo',
					show: !this.permissions.inventory.isNone
				},
				{
					name: 'simulations',
					title: this.$t('components.simulations'),
					icon: 'mdi-flask-outline',
					show: !this.permissions.inventory.isNone
				},
				{
					name: 'settings',
					title: this.$t('components.settings'),
					icon: 'mdi-cogs',
					show:
						!this.permissions.accounts.isNone ||
						!this.permissions.items.isNone ||
						!this.permissions.users.isReadWrite
				},
			];

			groups.forEach(group => {
				const pages = this.pages.filter(c => c.show && c.group === group.name);
				if (pages.length > 0) {
					groupsPages.push({
						title: group.title,
						icon: group.icon,
						pages
					})
				}
			});

			return groupsPages;
		},

		pages() {
			return [
				{
					title: this.$T('routes.purchases'),
					target: 'purchases',
					icon: 'mdi-cart-percent',
					disabled: false,
					show: !this.permissions.purchases.isNone,
				},
				{
					title: this.$T('routes.receiving'),
					target: 'receiving',
					icon: 'mdi-warehouse',
					disabled: false,
					show: !this.permissions.receives.isNone,
				},
				{
					title: this.$T('routes.sales'),
					target: 'sales',
					icon: 'mdi-point-of-sale',
					disabled: false,
					show: !this.permissions.sales.isNone,
				},
				{
					title: this.$T('routes.production-orders'),
					target: 'production-orders',
					icon: 'mdi-receipt-text-send',
					disabled: false,
					group: 'productions',
					show: !this.permissions.managingProductionOrders.isNone,
				},
				{
					title: this.$T('routes.order-results'),
					target: 'order-results',
					icon: 'mdi-filter-check',
					disabled: false,
					group: 'productions',
					show: !this.permissions.managingOrderResults.isNone,
				},
				{
					title: this.$T('routes.inventory'),
					target: 'inventory',
					icon: 'mdi-warehouse',
					group: 'warehouse',
					disabled: false,
					show: !this.permissions.inventory.isNone,
				},
				{
					title: this.$T('routes.items-statement'),
					target: 'items-statement',
					icon: 'mdi-file-arrow-left-right',
					group: 'warehouse',
					disabled: false,
					show: !this.permissions.managingItemsStatement.isNone,
				},
				{
					title: this.$T('routes.receiving-journals'),
					target: 'receiving-journals',
					icon: 'mdi-receipt-text-arrow-left',
					disabled: false,
					group: 'warehouse',
					show: !this.permissions.managingReceivingJournals.isNone,
				},
				/* {
					title: this.$T('routes.raw-cardamom-sorting-journals'),
					target: 'raw-cardamom-sorting-journals',
					icon: 'mdi-receipt-text-send',
					disabled: false,
					group: 'warehouse',
					show: !this.permissions.inventory.isNone,
				},
				{
					title: this.$T('routes.cardamom-sorting-journals'),
					target: 'cardamom-sorting-journals',
					icon: 'mdi-filter-check',
					disabled: false,
					group: 'warehouse',
					show: !this.permissions.inventory.isNone,
				}, */
				{
					title: this.$T('routes.delivery-journals'),
					target: 'delivery-journals',
					icon: 'mdi-receipt-text-arrow-right',
					disabled: false,
					group: 'warehouse',
					show: !this.permissions.managingDeliveryJournals.isNone,
				},
				{
					title: this.$T('routes.material-journals'),
					target: 'material-journals',
					icon: 'mdi-invoice-list-outline',
					disabled: false,
					group: 'warehouse',
					show: this.permissions.materialJournal.isReadWrite || this.isAdmin,
				},
				{
					title: this.$T('routes.statistics'),
					target: 'statistics',
					icon: 'mdi-finance',
					disabled: false,
					show: !this.permissions.statistics.isNone,
				},
				{
					title: this.$T('routes.accounts'),
					target: 'accounts',
					icon: 'mdi-account-group',
					disabled: false,
					show: !this.permissions.accounts.isNone,
				},
				{
					title: this.$T('routes.items'),
					target: 'items',
					icon: 'mdi-sprout',
					disabled: false,
					show: !this.permissions.items.isNone,
				},
				{
					title: this.$T('routes.experimental-simulation'),
					target: 'experimental-simulation',
					icon: 'mdi-network-pos',
					disabled: false,
					group: 'simulations',
					show: !this.permissions.managingExperimentalSimulation.isNone,
				},
				{
					title: this.$T('routes.material-simulation'),
					target: 'material-simulation',
					icon: 'mdi-scale-unbalanced',
					disabled: false,
					group: 'simulations',
					show: !this.permissions.managingMaterialSimulation.isNone,
				},
				{
					title: this.$T('routes.general-settings'),
					target: 'general-settings',
					icon: 'mdi-cog',
					group: 'settings',
					disabled: false,
					show: (!this.permissions.accounts.isNone || !this.permissions.items.isNone) && !this.permissions.managingSettings.isNone,
				},
				{
					title: this.$T('routes.fiscal-years'),
					target: 'fiscal-years',
					icon: 'mdi-file-cog',
					group: 'settings',
					disabled: false,
					show: !this.permissions.fiscalYears.isNone,
				},
				{
					title: this.$T('routes.roles-management'),
					target: 'roles-management',
					icon: 'mdi-account-cog',
					group: 'settings',
					disabled: false,
					show: !this.permissions.users.isNone || !this.permissions.managingSettings.isNone,
				},
			]
		},

		localeValue: {
			get() {
				return this.value;
			},
			set(newVal) {
				this.$emit('input', newVal)
			}
		}
	},
}
</script>

<style lang="scss">
.powered-by {
	text-align: center;
	* {
		font-size: 11px;
	}
	a {
		color: inherit;
		text-decoration: none;
		font-weight: 500;
		text-transform: uppercase;
		letter-spacing: 1px;
	}
}
</style>